<script setup lang="ts">
import { inject, ref, onMounted, Ref, toValue, h } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { getEnvVar } from '@/utils/env';
import { formatDate, getMonthYear } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useViewComposable, useModalComposable } from '@/views/composable';
import { usePaymentStore } from '@/store/PaymentStore';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import LoadingSpinner from '../LoadingSpinner.vue';
import PaymentHistory from './PaymentHistory.vue';
import PaymentStatusModal from './PaymentStatusModal.vue';
import RetrievingModal from './RetrievingModal.vue';

const dialogRef = inject('dialogRef') as Ref;
const quote = dialogRef?.value?.data.quote;
const isSave = dialogRef?.value?.options?.emits?.isSave;

const dialog = useDialog();
const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const route = useRoute();
const userInfo = useUserInfo();
const PaymentStore = usePaymentStore();
const { renderAmount, renderAmountAccordingRouter } = useViewComposable();
const { handleOpenErrorMessage } = useModalComposable();
const currentUser = ref<UserInfo>();
const isLoading = ref(false);
const isProgress = ref(false);
const isSuccess = ref(false);
const expiredDate = ref();

const currentLanguage = getCurrentLocale();
const amount = ref<number>(0);
const cardName = ref('');

const displayPaymentStatusModal = (isSuccess: boolean, authNumber: string, amountNumber: number) => {
  const dialogRef = dialog.open(PaymentStatusModal, {
    data: {
      isSuccess: isSuccess,
      authNumber: authNumber,
      amount: amountNumber
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    },
    templates: {
      header: () => {
        return [h('div')];
      }
    }
  });
};

const getPaymentHistory = (quote: any) => {
  PaymentStore.getPaymentHistory(quote.quoteID)
    .then((response) => {
      isLoading.value = true;
      openPaymentHistory(response);
    })
    .catch((error) => {
      handleOpenErrorMessage(error);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const openPaymentHistory = (history: any) => {
  const dialogRef = dialog.open(PaymentHistory, {
    props: {
      modal: true
    },
    data: {
      history
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleValidateForm = async () => {
  try {
    const ccToken = document.getElementById('ccNum')?.value;
    const ccvToken = document.getElementById('ccCVV')?.value;

    if (!amount.value) {
      handleOpenErrorMessage(t('notification.please_fill_amount'));
      return;
    }

    if (!ccToken || !ccvToken) {
      handleOpenErrorMessage(t('notification.please_fill_credit'));
      return;
    }

    await handleCallSubmitForm(ccToken, ccvToken);
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.submit_error'), life: 5000 });
    isSave(false);
  }
};

const handleCallSubmitForm = async (ccToken: string, ccvToken: string) => {
  const quoteIdArray = quote?.quoteID?.split(',');
  isLoading.value = true;
  isProgress.value = true;
  const payload = {
    quoteId: quoteIdArray?.[0],
    email: toValue(currentUser)?.email,
    ccToken,
    ccvToken,
    bookingNum: quote?.bookingId,
    amount: amount.value,
    expiryDate: getMonthYear(expiredDate.value),
    cardName: cardName.value
  };

  await PaymentStore.submit(payload)
    .then((response) => {
      if (response.status === 'success') {
        isSuccess.value = true;
        toast.add({ severity: 'success', summary: '', detail: t('notification.submit_success'), life: 3000 });
        isSave(true);
        displayPaymentStatusModal(isSuccess.value, response.authNumber, amount.value);
      } else {
        isSuccess.value = false;
        handleOpenErrorMessage(response.message);
        isSave(false);
        displayPaymentStatusModal(isSuccess.value, response.authNumber, amount.value);
      }
    })
    .catch(() => {
      isSuccess.value = false;
      toast.add({ severity: 'error', summary: '', detail: t('notification.submit_error'), life: 5000 });
      isSave(false);
    })
    .finally(() => {
      isLoading.value = false;
      isProgress.value = false;
    });
};

onMounted(async () => {
  currentUser.value = await userInfo.getUserInfo();
  const parenthst = window.location.protocol + '//' + window.location.host;
  const iFrame1 = document.getElementById('ccframe');
  const language = getTextVariant(currentLanguage, 'en_CA', 'fr_CA');
  const siteId = getEnvVar('VITE_HPCI_SITE_ID');
  if (iFrame1 && typeof siteId === `string`) {
    iFrame1.src = `https://cc1.na1.networkhpci.com/iSynSApp/showPxyPage!ccFrame.action?pgmode1=&enable3DSec=skip3ds&selected3DSecPayName=&selected3DSecPayCurISO=&enableEarlyToken=Y&enableTokenDisplay=Y&reportCCType=Y&reportCCDigits=Y&reportCVVDigits=Y&strictMsgFmt=Y&locationName=GQTCheckout&ccFrameName=ccframe&sid=${siteId}&fullParentHost=${parenthst}&fullParentQStr=/external/myquotes&ccTypeMapMode=def&formatCCDigits=Y&formatCCDigitsDelimiter=-&formatCCMaskMode=S2L2&formatCCMaskChar=*&reportInit=Y&dataType=achus1&lang=${language}`;
  }
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="payment">
    <div class="payment-modal">
      <div class="detail-booking mb-4">
        <div class="flex justify-content-between gap-3">
          <p class="font-bold text-900">{{ `${$t('payment.labels.deposit_amount')} : (${$t('common.due')} ${formatDate(quote?.depositDate, 'dd-LLL-yyyy', false, currentLanguage)})` }}</p>
          <p class="font-bold text-900">{{ renderAmount(quote?.depositAmount) }}</p>
        </div>
        <div class="flex justify-content-between gap-3">
          <p class="font-bold text-900">{{ `${$t('payment.labels.total_amount_due')} : (${$t('common.due')} ${formatDate(quote?.finalPaymentDate, 'dd-LLL-yyyy', false, currentLanguage)})` }}</p>
          <p class="font-bold text-900">{{ renderAmountAccordingRouter(quote?.finalPayment, route?.name) }}</p>
        </div>
        <div class="flex justify-content-between gap-3">
          <p class="font-bold text-900">{{ `${$t('payment.labels.total_amount_paid')} :` }}</p>
          <p class="font-bold text-900">{{ renderAmount(quote?.totalAmountPaid) }}</p>
        </div>
        <div class="flex justify-content-between gap-3">
          <p class="font-bold text-900">{{ `${$t('payment.labels.remaining_amount_due')} : ` }}</p>
          <p class="font-bold text-900">{{ renderAmountAccordingRouter(quote?.remainingAmountDue, route?.name) }}</p>
        </div>
      </div>
      <div class="mb-4">
        <div class="flex justify-content-between gap-3">
          <div></div>
          <p class="font-bold history" @click="getPaymentHistory(quote)">{{ $t('payment.labels.view_history') }}</p>
        </div>
      </div>
      <div class="grid">
        <div class="col flex flex-col gap-2">
          <label class="label" for="bookingid">{{ $t('payment.labels.creditName') }}</label>
          <InputText id="bookingid" :min="1" v-model="cardName" />
        </div>
      </div>
      <div class="grid">
        <div class="col flex flex-col gap-2">
          <form id="CCAcceptForm">
            <div class="form-group">
              <iframe title="payment" id="ccframe" onload="receiveHPCIMsg()" name="ccframe" class="border-none w-full height-220px" />
            </div>
          </form>
          <div class="border-token-div grid hidden">
            <div class="token-div col-6">
              <div class="right-side">
                <InputText id="ccNum" name="cc-token" readonly value="" />
              </div>
            </div>
            <div class="token-div col-6">
              <div class="right-side">
                <InputText id="ccCVV" name="cvv-token" readonly value=" " />
              </div>
            </div>
            <div class="token-div col-6">
              <div class="right-side">
                <InputText id="ccBIN" name="cc-bin" readonly value="" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="grid">
        <div class="col flex flex-col col-6">
          <div class="left-side">
            <div class="label">{{ $t('payment.labels.expire') }}</div>
          </div>
          <div class="right-side">
            <DatePicker class="w-full" view="month" dateFormat="mm/yy" v-model="expiredDate">
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
          </div>
        </div>
        <div class="col flex flex-col col-6">
          <div class="left-side">
            <div class="label">{{ $t('payment.labels.amount') }}</div>
          </div>
          <div class="right-side">
            <InputNumber class="w-full" currency="USD" locale="en-US" mode="currency" :min="1" v-model="amount" />
          </div>
        </div>
      </div>
      <br />

      <div class="text-center mt-5">
        <Button :loading="isLoading" id="payment-btn" :label="$t('payment.labels.submit')" @click="handleValidateForm()"></Button>
      </div>
    </div>
    <Dialog v-model:visible="isProgress" modal>
      <template #header>
        <div></div>
      </template>
      <RetrievingModal :title="$t('payment.process')" :subTitle="$t('common.wait')" />
    </Dialog>
  </div>
</template>
